// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { MyAccountPageContext } from "@insite/content-library/Pages/MyAccountPage";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadAccountStats from "@mcmc/App/Handlers/MyAccount/LoadAccountStats";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getAccountStats } from "@mcmc/App/Store/Selectors/AccountStatsSelectors";
import { styledBox } from "@mcmc/XDL/Components/Common/Box";
import AvgPayDays from "@mcmc/XDL/Icons/AvgPayDays";
import CreditLimit from "@mcmc/XDL/Icons/CreditLimit";
import Date from "@mcmc/XDL/Icons/Date";
import LastPaymentAmt from "@mcmc/XDL/Icons/LastPaymentAmt";
import OrderBalance from "@mcmc/XDL/Icons/OrderBalance";
import Terms from "@mcmc/XDL/Icons/Terms";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: McMcApplicationState) => ({
    accountStats: getAccountStats(state),
    session: state.context.session,
});

const mapDispatchToProps = {
    loadAccountStats,
};
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountStatsStyles {
    mainWrapper?: InjectableCss;
    itemLabel?: TypographyProps;
    itemValue?: TypographyProps;
    itemDescriptiveIcon?: IconProps;
    itemWrapper?: InjectableCss;
    titleText?: TypographyProps;
    itemsContainer?: InjectableCss;
}

export const accountStatsStyles: AccountStatsStyles = {
    itemWrapper: {
        css: css`
            display: flex;
            border: 1px solid ${getColor("common.ui3")};
            border-left: 15px solid ${getColor("common.border")};
            position: relative;
            padding: 20px 15px;
            margin: 10px;
            flex: 1 0 calc((100% - 60px) / 3);
            min-width: 240px;
            flex-direction: column;
            justify-content: center;

            &:nth-of-type(3n) {
                border-left-color: ${getColor("primary.main")};
            }

            &:nth-of-type(3n-1) {
                border-left-color: ${getColor("secondary.main")};
            }
        `,
    },
    mainWrapper: {
        css: css`
            ${styledBox};
            margin: 30px 0;
        `,
    },
    itemLabel: {
        weight: "bold",
        css: css`
            display: block;
            max-width: calc(100% - 40px);
        `,
    },
    itemValue: {
        as: "p",
        css: css`
            max-width: calc(100% - 40px);
            padding-top: 3px;
        `,
    },
    itemDescriptiveIcon: {
        color: "common.ui2",
        size: 38,
        css: css`
            position: absolute;
            transform: translateY(-50%);
            top: 50%;
            right: 12px;
        `,
    },
    titleText: {
        css: css`
            margin-bottom: 25px;
        `,
    },
    itemsContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            padding: 0;
            margin: -10px;
        `,
    },
};

const styles = accountStatsStyles;

const AccountStats: React.FC<Props> = ({ accountStats, loadAccountStats, session }) => {
    useEffect(() => {
        if (session.userRoles.indexOf("Administrator") > -1 || session.userRoles.indexOf("Buyer3") > -1) {
            if (!accountStats) {
                loadAccountStats({});
            }
        }
    }, []);

    return (
        <>
            {accountStats && (
                <StyledWrapper {...styles.mainWrapper}>
                    <Typography variant="h2" {...styles.titleText}>
                        {translate("Account Stats")}
                    </Typography>
                    <StyledWrapper {...styles.itemsContainer}>
                        <StyledWrapper {...styles.itemWrapper}>
                            <Typography {...styles.itemLabel}>{translate("Credit Established On:")}</Typography>
                            <Typography {...styles.itemValue}>{accountStats.creditEstablishedDate}</Typography>
                            <Icon src={Date} {...styles.itemDescriptiveIcon} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.itemWrapper}>
                            <Typography {...styles.itemLabel}>{translate("Terms:")}</Typography>
                            <Typography {...styles.itemValue}>{accountStats.termsType}</Typography>
                            <Icon src={Terms} {...styles.itemDescriptiveIcon} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.itemWrapper}>
                            <Typography {...styles.itemLabel}>{translate("Credit Limit:")}</Typography>
                            <Typography {...styles.itemValue}>{accountStats.creditLimitDisplay}</Typography>
                            <Icon src={CreditLimit} {...styles.itemDescriptiveIcon} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.itemWrapper}>
                            <Typography {...styles.itemLabel}>{translate("Order Balance:")}</Typography>
                            <Typography {...styles.itemValue}>{accountStats.orderBalanceDisplay}</Typography>
                            <Icon src={OrderBalance} {...styles.itemDescriptiveIcon} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.itemWrapper}>
                            <Typography {...styles.itemLabel}>{translate("Last Payment Amount:")}</Typography>
                            <Typography {...styles.itemValue}>
                                {accountStats.lastPaymentAmountDisplay} {translate("on ")}
                                {accountStats.lastPaymentDate}
                            </Typography>
                            <Icon src={LastPaymentAmt} {...styles.itemDescriptiveIcon} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.itemWrapper}>
                            <Typography {...styles.itemLabel}>{translate("Average Pay Days:")}</Typography>
                            <Typography {...styles.itemValue}>
                                {accountStats.avgPayDays} {translate("Days")}
                            </Typography>
                            <Icon src={AvgPayDays} {...styles.itemDescriptiveIcon} />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const accountStatsWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountStats),
    definition: {
        allowedContexts: [MyAccountPageContext],
        group: "My Account",
    },
};

export default accountStatsWidget;
